<template>
  <div class="reader-container">
    <!-- 顶部工具栏 -->
    <div class="reader-toolbar">
      <div class="toolbar-left">
        <el-button type="text" @click="goBack">
          <el-icon><arrow-left /></el-icon>
          返回
        </el-button>
        <h2>{{ book.title }}</h2>
        <span v-if="book.author" class="book-author">作者：{{ book.author }}</span>
      </div>
      <div class="toolbar-right">
        <span class="progress-info">
          进度：{{ progressPercentage }}% (第 {{ currentPage }} 页 / 共 {{ totalPages }} 页)
        </span>
        <el-dropdown @command="handleMenu">
          <el-button>
            <el-icon><Menu /></el-icon>
            操作
            <el-icon><arrow-down /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="prev" :disabled="currentPage === 1">
                <el-icon><ArrowLeft /></el-icon>
                上一页
              </el-dropdown-item>
              <el-dropdown-item command="next" :disabled="!hasNext">
                下一页
                <el-icon><ArrowRight /></el-icon>
              </el-dropdown-item>
              <el-dropdown-item divided command="bookmark">
                <el-icon><CollectionTag /></el-icon>
                添加书签
              </el-dropdown-item>
              <el-dropdown-item command="bookmarks">
                <el-icon><Collection /></el-icon>
                书签列表
              </el-dropdown-item>
              <el-dropdown-item command="jump">
                <el-icon><Position /></el-icon>
                跳转到页码
              </el-dropdown-item>
              <el-dropdown-item divided command="font">
                <span>字体大小：{{ fontSize }}</span>
              </el-dropdown-item>
              <el-dropdown-item v-for="size in fontSizes" :key="size" :command="'font-' + size">
                {{ size }}px
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <div class="reader-content">
      <!-- 左侧书签面板 -->
      <div v-if="showBookmarks" :class="['bookmarks-panel', { show: showBookmarks }]">
        <div class="panel-header">
          <h3>书签列表</h3>
          <el-button type="text" @click="showBookmarks = false">
            <el-icon><close /></el-icon>
          </el-button>
        </div>
        <div class="bookmarks-list">
          <div
            v-for="bookmark in bookmarks"
            :key="bookmark.id"
            class="bookmark-item"
            @click="jumpToPosition(bookmark.position)"
          >
            <div class="bookmark-name">{{ bookmark.name }}</div>
            <div class="bookmark-position">页码：{{ Math.floor(bookmark.position / pageSize) + 1 }}</div>
            <div v-if="bookmark.note" class="bookmark-note">{{ bookmark.note }}</div>
            <div class="bookmark-actions">
              <el-button type="text" size="small" @click.stop="editBookmark(bookmark)">
                编辑
              </el-button>
              <el-button type="text" size="small" @click.stop="deleteBookmark(bookmark.id)">
                删除
              </el-button>
            </div>
          </div>
          <div v-if="bookmarks.length === 0" class="no-bookmarks">
            暂无书签
          </div>
        </div>
      </div>

      <!-- 阅读区域 -->
      <div class="text-content" :style="{ fontSize: fontSize + 'px' }" ref="textContainer">
        <div v-if="loading" class="loading">
          <el-icon class="loading-icon"><loading /></el-icon>
          <p>加载中...</p>
        </div>
        <div v-else-if="error" class="error">
          <el-icon><warning /></el-icon>
          <p>{{ error }}</p>
          <el-button @click="loadContent">重试</el-button>
        </div>
        <div v-else>
          <div class="text-page">
            {{ currentContent }}
          </div>
          <div class="navigation">
            <el-button
              :disabled="currentPage === 1"
              @click="prevPage"
            >
              上一页
            </el-button>
            <el-button
              :disabled="!hasNext"
              @click="nextPage"
            >
              下一页
            </el-button>
          </div>
        </div>
      </div>

    </div>

    <!-- 添加/编辑书签对话框 -->
    <el-dialog
      v-model="showBookmarkDialog"
      :title="editingBookmark ? '编辑书签' : '添加书签'"
      width="400px"
    >
      <el-form ref="bookmarkForm" :model="bookmarkForm" :rules="bookmarkRules" label-width="80px">
        <el-form-item label="名称" prop="name">
          <el-input v-model="bookmarkForm.name" placeholder="请输入书签名称" />
        </el-form-item>
        <el-form-item label="页码">
          <el-input-number
            v-model="bookmarkForm.position"
            :min="1"
            :max="totalPages"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="bookmarkForm.note"
            type="textarea"
            :rows="3"
            placeholder="请输入备注"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showBookmarkDialog = false">取消</el-button>
          <el-button type="primary" @click="saveBookmark">
            {{ editingBookmark ? '更新' : '添加' }}
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 跳转页码对话框 -->
    <el-dialog
      v-model="showJumpDialog"
      title="跳转到页码"
      width="300px"
    >
      <el-form ref="jumpForm" :model="jumpForm" :rules="jumpRules" label-width="80px">
        <el-form-item label="当前页" prop="page">
          <el-input-number
            v-model="jumpForm.page"
            :min="1"
            :max="totalPages"
            :step="1"
            style="width: 100%"
            placeholder="请输入页码"
          />
        </el-form-item>
        <div class="jump-info">
          当前：第 {{ currentPage }} 页，共 {{ totalPages }} 页
        </div>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showJumpDialog = false">取消</el-button>
          <el-button type="primary" @click="jumpToPage">跳转</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api'
import {
  ArrowLeft, Collection, CollectionTag, ArrowDown, ArrowRight,
  Close, Loading, Warning, Position, Menu
} from '@element-plus/icons-vue'

export default {
  name: 'Reader',
  components: {
    ArrowLeft, Collection, CollectionTag, ArrowDown, ArrowRight,
    Close, Loading, Warning, Position, Menu
  },
  props: {
    bookId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      book: {},
      loading: true,
      error: null,
      currentContent: '',
      currentPage: 1,  // 当前页码（从 1 开始）
      totalChars: 0,
      hasNext: false,
      pageSize: 2000,
      showBookmarks: false,
      bookmarks: [],
      showBookmarkDialog: false,
      editingBookmark: null,
      bookmarkForm: {
        name: '',
        position: 0,
        note: ''
      },
      bookmarkRules: {
        name: [{ required: true, message: '请输入书签名称', trigger: 'blur' }]
      },
      fontSize: 16,
      fontSizes: [12, 16, 24, 32, 48, 60],
      progressPercentage: 0,
      totalPages: 1,  // 总页数
      showJumpDialog: false,
      jumpForm: {
        page: 1
      },
      jumpRules: {
        page: [{ required: true, message: '请输入页码', trigger: 'blur' }]
      }
    }
  },
  async mounted() {
    await this.loadBookInfo()

    // 先检查路由参数中是否有 page 参数（从书籍列表跳转时传递的）
    if (this.$route.query.page) {
      this.currentPage = parseInt(this.$route.query.page) || 1
      console.log('从路由参数设置 currentPage:', this.currentPage)
    } else {
      // 否则从后端加载阅读进度
      await this.loadReadingProgress()
    }

    // 再加载内容（会计算 totalPages 和 progressPercentage）
    await this.loadContent()
    console.log('加载后：currentPage=', this.currentPage, 'totalPages=', this.totalPages)

    await this.loadBookmarks()
  },
  beforeUnmount() {
    this.saveProgress()
  },
  methods: {
    async loadBookInfo() {
      try {
        this.book = await api.getBook(this.bookId)
      } catch (error) {
        console.error('获取书籍信息失败:', error)
        this.$message.error('获取书籍信息失败')
      }
    },
    async loadContent() {
      this.loading = true
      this.error = null
      try {
        // 根据页码计算字符位置
        const startPosition = (this.currentPage - 1) * this.pageSize
        const params = {
          start: startPosition,
          length: this.pageSize
        }
        const data = await api.getBookContent(this.bookId, params)
        this.currentContent = data.content
        this.totalChars = data.total
        this.hasNext = data.has_next

        // 使用后端返回的页码信息
        this.totalPages = data.total_pages || Math.max(1, Math.ceil(this.totalChars / this.pageSize))
        this.currentPage = data.current_page || this.currentPage

        // 计算进度百分比（currentPage 从 1 开始，所以要减 1）
        this.progressPercentage = this.totalChars > 0
          ? Math.round(((this.currentPage - 1) / this.totalPages) * 100)
          : 0

        console.log('loadContent:', { currentPage: this.currentPage, totalPages: this.totalPages, progressPercentage: this.progressPercentage })
      } catch (error) {
        console.error('加载内容失败:', error)
        this.error = error.response?.data?.detail || '加载内容失败'
      } finally {
        this.loading = false
      }
    },
    async loadBookmarks() {
      try {
        this.bookmarks = await api.getBookBookmarks(this.bookId)
      } catch (error) {
        console.error('加载书签失败:', error)
      }
    },
    async loadReadingProgress() {
      try {
        const progress = await api.getReadingProgress(this.bookId)
        console.log('加载阅读进度:', progress)
        // 从后端获取的是页码，直接设置当前页
        const savedPage = progress.page || 1
        this.currentPage = savedPage > 0 ? savedPage : 1
        console.log('设置 currentPage:', this.currentPage)
      } catch (error) {
        console.error('加载阅读进度失败:', error)
      }
    },
    async saveProgress() {
      try {
        await api.updateReadingProgress(this.bookId, {
          page: this.currentPage,
          progress_percentage: this.progressPercentage
        })
        this.$message.success('进度已保存')
      } catch (error) {
        console.error('保存进度失败:', error)
      }
    },
    async addBookmark() {
      this.editingBookmark = null
      this.bookmarkForm = {
        name: `书签${this.bookmarks.length + 1}`,
        position: this.currentPage,  // 使用当前页码
        note: ''
      }
      this.showBookmarkDialog = true
    },
    editBookmark(bookmark) {
      this.editingBookmark = bookmark
      this.bookmarkForm = {
        name: bookmark.name,
        position: bookmark.position,
        note: bookmark.note || ''
      }
      this.showBookmarkDialog = true
    },
    async saveBookmark() {
      this.$refs.bookmarkForm.validate(async (valid) => {
        if (!valid) return

        try {
          // 将页码转换为字符位置 (页码从 1 开始，字符位置从 0 开始)
          const charPosition = (this.bookmarkForm.position - 1) * this.pageSize

          if (this.editingBookmark) {
            await api.updateBookmark(this.editingBookmark.id, {
              ...this.bookmarkForm,
              position: charPosition
            })
            this.$message.success('书签更新成功')
          } else {
            await api.createBookmark({
              ...this.bookmarkForm,
              position: charPosition,
              book_id: parseInt(this.bookId)
            })
            this.$message.success('书签添加成功')
          }
          this.showBookmarkDialog = false
          this.loadBookmarks()
        } catch (error) {
          console.error('保存书签失败:', error)
          this.$message.error(error.response?.data?.detail || '保存书签失败')
        }
      })
    },
    async deleteBookmark(bookmarkId) {
      try {
        await this.$confirm('确定删除此书签吗？', '提示', {
          type: 'warning'
        })
        await api.deleteBookmark(bookmarkId)
        this.$message.success('书签删除成功')
        this.loadBookmarks()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除书签失败:', error)
          this.$message.error('删除书签失败')
        }
      }
    },
    jumpToPosition(position) {
      // position 是字符位置，需要转换为页码 (页码从 1 开始)
      const pageNum = Math.floor(position / this.pageSize) + 1
      this.currentPage = pageNum > 0 ? pageNum : 1
      this.loadContent()
      this.showBookmarks = false
    },
    prevPage() {
      this.currentPage = Math.max(1, this.currentPage - 1)
      this.loadContent()
      this.saveProgress()
    },
    nextPage() {
      if (this.hasNext) {
        this.currentPage += 1
        this.loadContent()
        this.saveProgress()
      }
    },
    onProgressChange(value) {
      // value 是页码
      this.currentPage = value
      this.loadContent()
      this.saveProgress()
    },
    handleMenu(command) {
      if (command === 'prev') {
        this.prevPage()
      } else if (command === 'next') {
        this.nextPage()
      } else if (command === 'bookmark') {
        this.addBookmark()
      } else if (command === 'bookmarks') {
        this.showBookmarks = !this.showBookmarks
      } else if (command === 'jump') {
        this.showJumpDialog = true
      } else if (command.startsWith('font-')) {
        const size = parseInt(command.replace('font-', ''))
        this.fontSize = size
      }
    },
    jumpToPage() {
      this.$refs.jumpForm.validate(async (valid) => {
        if (!valid) return
        this.currentPage = this.jumpForm.page
        this.loadContent()
        this.saveProgress()
        this.showJumpDialog = false
      })
    },
    goBack() {
      this.$router.push('/books')
    }
  }
}
</script>

<style scoped>
.reader-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.reader-toolbar {
  background-color: #fff;
  padding: 10px 15px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
  gap: 10px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.toolbar-left h2 {
  margin: 0;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

.book-author {
  color: #666;
  font-size: 12px;
  white-space: nowrap;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.progress-info {
  color: #666;
  font-size: 13px;
  white-space: nowrap;
  padding: 0 8px;
}

.reader-content {
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
}

.bookmarks-panel {
  width: 280px;
  background-color: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 10;
  transition: transform 0.3s ease;
}

.panel-header {
  padding: 12px 15px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.panel-header h3 {
  margin: 0;
  font-size: 15px;
}

.bookmarks-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.bookmark-item {
  padding: 12px;
  margin-bottom: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.bookmark-item:hover {
  background-color: #f5f7fa;
}

.bookmark-item:active {
  background-color: #e8f3ff;
}

.bookmark-name {
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 14px;
}

.bookmark-position {
  font-size: 12px;
  color: #666;
  margin-bottom: 5px;
}

.bookmark-note {
  font-size: 12px;
  color: #999;
  font-style: italic;
  margin-bottom: 8px;
}

.bookmark-actions {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.no-bookmarks {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.text-content {
  flex: 1;
  padding: 15px;
  overflow: auto;
  line-height: 1.8;
  background-color: #f9f9f9;
  -webkit-overflow-scrolling: touch;
  height: 100%;
}

.text-page {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 100%;
  margin: 0 auto;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-size: inherit;
  min-height: 100%;
}

.navigation {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  margin: 20px auto 0;
  gap: 15px;
  flex-wrap: wrap;
  padding-bottom: 20px;
}

.navigation .el-button {
  min-width: 120px;
  height: 48px;
  font-size: 16px;
  padding: 0 24px;
}

.progress-bar {
  padding: 10px 15px;
  background-color: white;
  border-top: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.loading, .error {
  text-align: center;
  padding: 60px 20px;
}

.loading-icon {
  font-size: 36px;
  color: #409EFF;
  animation: rotating 2s linear infinite;
  margin-bottom: 15px;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error .el-icon {
  font-size: 36px;
  color: #F56C6C;
  margin-bottom: 15px;
}

.jump-info {
  color: #909399;
  font-size: 13px;
  text-align: center;
  margin-top: 10px;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .reader-toolbar {
    padding: 8px 10px;
  }

  .toolbar-left h2 {
    font-size: 14px;
  }

  .progress-info {
    font-size: 12px;
    padding: 0 5px;
  }

  .toolbar-right .el-button {
    padding: 8px 12px;
    font-size: 13px;
  }

  .toolbar-right .el-button .el-icon {
    margin-right: 3px;
  }

  .bookmarks-panel {
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transform: translateX(-100%);
  }

  .bookmarks-panel.show {
    transform: translateX(0);
  }

  .text-content {
    padding: 10px;
  }

  .text-page {
    padding: 15px;
  }

  .navigation {
    flex-direction: row;
    gap: 15px;
    padding-bottom: 15px;
  }

  .navigation .el-button {
    flex: 1;
    max-width: 200px;
    height: 52px;
    font-size: 17px;
    min-width: 140px;
  }

  .progress-bar {
    padding: 8px 10px;
  }
}

@media screen and (max-width: 480px) {
  .toolbar-left h2 {
    max-width: 100px;
  }

  .book-author {
    display: none;
  }

  .progress-info {
    display: none;
  }

  .toolbar-right .el-button {
    padding: 8px;
  }

  .toolbar-right .el-button span:not(.el-icon) {
    display: none;
  }

  .text-page {
    padding: 12px;
  }

  .navigation {
    gap: 12px;
  }

  .navigation .el-button {
    height: 56px;
    font-size: 18px;
    min-width: 150px;
    max-width: 100%;
  }
}
</style>
